import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import Select from "react-select";
import Swal from "sweetalert2";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import ReplyAllRoundedIcon from "@mui/icons-material/ReplyAllRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RestoreFromTrashRoundedIcon from "@mui/icons-material/RestoreFromTrashRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import QuillResizeImage from "quill-resize-image";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import apiurl from "../Api";
import "./css/advancePayment.css";
import "./css/mails.css";

Quill.register("modules/resize", QuillResizeImage);

// Defined once at module scope, not inline in JSX — Quill treats a new `modules` object
// identity on every render as a reason to reinitialize the editor, which resets the cursor
// position while typing.
const SIGNATURE_EDITOR_MODULES = {
  toolbar: [
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
  resize: {
    locale: {},
  },
};

// Outlook-style sidebar: same 4 folders repeated under Inbox and Sent.
const folderTypes = [
  { key: "others", label: "All", icon: "ri-file-list-3-line" },
  { key: "payroll", label: "Payroll", icon: "ri-secure-payment-line" },
  { key: "infratel", label: "Infratel", icon: "ri-building-2-line" },
  { key: "brms", label: "BRMS", icon: "ri-building-4-line" },
];

const progressOptions = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const sectionToggleOptionsByTab = {
  payroll: [
    { value: "", label: "All" },
    { value: "invoice", label: "Invoice Generation" },
    { value: "payment", label: "Payments" },
    { value: "uncoded", label: "Payroll Uncoded" },
  ],
  others: [
    { value: "", label: "All" },
    { value: "invoice", label: "Invoice Generation" },
    { value: "payment", label: "Payments" },
  ],
  infratel: [
    { value: "", label: "All" },
    { value: "invoice", label: "Invoice Generation" },
    { value: "payment", label: "Payments" },
  ],
  brms: [
    { value: "", label: "All" },
    { value: "invoice", label: "Invoice Generation" },
    { value: "payment", label: "Payments" },
  ],
};

const progressFilterOptions = [{ value: "", label: "New arrived" }, ...progressOptions];

const priorityOptions = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const priorityFilterOptions = [{ value: "", label: "All Priority" }, ...priorityOptions];

const dateRangeOptions = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "custom", label: "Custom Range" },
];

function toDateInputValue(date) {
  return date.toISOString().slice(0, 10);
}

function computeDateRange(preset, customFrom, customTo) {
  const today = new Date();
  if (preset === "today") {
    const d = toDateInputValue(today);
    return { from: d, to: d };
  }
  if (preset === "7d") {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return { from: toDateInputValue(start), to: toDateInputValue(today) };
  }
  if (preset === "30d") {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { from: toDateInputValue(start), to: toDateInputValue(today) };
  }
  if (preset === "custom") {
    return { from: customFrom, to: customTo };
  }
  return { from: "", to: "" };
}

function fileIcon(name, className) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return <PictureAsPdfRoundedIcon className={`${className} text-danger`} />;
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    return <ImageRoundedIcon className={`${className} text-primary`} />;
  }
  if (["xls", "xlsx", "csv"].includes(ext)) {
    return <TableChartRoundedIcon className={`${className} text-success`} />;
  }
  if (["doc", "docx"].includes(ext)) {
    return <DescriptionRoundedIcon className={`${className} text-info`} />;
  }
  return <InsertDriveFileRoundedIcon className={`${className} text-secondary`} />;
}

function AttachmentTile({ name, label, onView, onDownload, large }) {
  return (
    <div className={`mails-attachment-tile ${large ? "mails-attachment-tile-lg" : ""}`} onClick={onView} title={name}>
      {onDownload && (
        <button
          type="button"
          className="mails-attachment-tile-download"
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          title="Download"
        >
          <i className="ri-download-2-line" />
        </button>
      )}
      {fileIcon(name, "mails-attachment-tile-icon")}
      <span className="mails-attachment-tile-name">
        {name}
        {label ? <span className="text-muted"> {label}</span> : null}
      </span>
    </div>
  );
}

function Mails() {
  const [activeSection, setActiveSection] = useState("inbox");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("others");
  const [sectionFilter, setSectionFilter] = useState("");
  const [progressFilter, setProgressFilter] = useState(progressFilterOptions[0]);
  const [priorityFilter, setPriorityFilter] = useState(priorityFilterOptions[0]);
  const [assignedFilter, setAssignedFilter] = useState({ value: "unassigned", label: "Unassigned" });
  const [dateRangePreset, setDateRangePreset] = useState(dateRangeOptions[0]);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;
  const [sentRows, setSentRows] = useState([]);
  const [sentTotal, setSentTotal] = useState(0);
  const [sentPage, setSentPage] = useState(1);
  const [sentSearch, setSentSearch] = useState("");
  const [sentLoading, setSentLoading] = useState(false);
  const [trashRows, setTrashRows] = useState([]);
  const [trashTotal, setTrashTotal] = useState(0);
  const [trashPage, setTrashPage] = useState(1);
  const [trashSearch, setTrashSearch] = useState("");
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashTotalCount, setTrashTotalCount] = useState(0);
  const [currentUser, setCurrentUser] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const [currentUserPost, setCurrentUserPost] = useState("");
  const [savedSignature, setSavedSignature] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureDraft, setSignatureDraft] = useState("");
  const [signaturePreviewExpanded, setSignaturePreviewExpanded] = useState(false);
  const [signatureSaving, setSignatureSaving] = useState(false);
  const [accountsStaff, setAccountsStaff] = useState([]);
  const [tabCounts, setTabCounts] = useState({ payroll: 0, infratel: 0, brms: 0, others: 0 });
  const [sentTabCounts, setSentTabCounts] = useState({ payroll: 0, infratel: 0, brms: 0, others: 0 });
  const [sectionCounts, setSectionCounts] = useState({ invoice: 0, payment: 0, uncoded: 0 });
  const [sentTotalCount, setSentTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previewMail, setPreviewMail] = useState(null);
  const [assigningId, setAssigningId] = useState(null);

  const isAccountsHead = currentUserPost === "accounts_head";
  const [sheetPreview, setSheetPreview] = useState(null);
  const [sheetActiveTab, setSheetActiveTab] = useState(0);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState("");
  const [detailMailId, setDetailMailId] = useState(null);
  const [detailMail, setDetailMail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attachmentsListRow, setAttachmentsListRow] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyCc, setReplyCc] = useState("");
  const replyCcRef = useRef(null);
  const [ownMailbox, setOwnMailbox] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replyLoadingId, setReplyLoadingId] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const [showQuoted, setShowQuoted] = useState(true);

  const isInlinePreviewable = (name) => /\.(pdf|jpg|jpeg|png)$/i.test(name || "");
  const isSpreadsheetPreviewable = (name) => /\.(xlsx?|csv)$/i.test(name || "");

  const buildListParams = useCallback(() => {
    const { from, to } = computeDateRange(dateRangePreset?.value, customFrom, customTo);
    const params = { mode: "list", type: activeTab, page, page_size: pageSize };
    if (sectionFilter) params.section = sectionFilter;
    if (progressFilter?.value) params.progress = progressFilter.value;
    if (priorityFilter?.value) params.priority = priorityFilter.value;
    if (assignedFilter?.value) params.assigned = assignedFilter.value;
    if (from) params.from = from;
    if (to) params.to = to;
    if (search.trim()) params.q = search.trim();
    return params;
  }, [activeTab, sectionFilter, progressFilter, priorityFilter, assignedFilter, dateRangePreset, customFrom, customTo, search, page]);

  const fetchMails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiurl}mails.php`, { params: buildListParams() });
      if (response.data?.status === true) {
        setRows(response.data.data || []);
        setTotal(response.data.total || 0);
        setCurrentUser(response.data.current_user || "");
        setCurrentUserName(response.data.current_user_name || "");
        setCurrentUserPost(response.data.current_user_post || "");
        setAccountsStaff(response.data.accounts_staff || []);
        setTabCounts(response.data.tab_counts || { payroll: 0, infratel: 0, brms: 0, others: 0 });
        setSectionCounts(response.data.section_counts || { invoice: 0, payment: 0, uncoded: 0 });
        setSentTotalCount(response.data.sent_total || 0);
        setTrashTotalCount(response.data.trash_total || 0);
        setOwnMailbox(response.data.own_mailbox || "");
      } else {
        setRows([]);
        setTotal(0);
      }
    } catch (error) {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildListParams]);

  const refreshSilently = useCallback(async () => {
    try {
      const response = await axios.get(`${apiurl}mails.php`, { params: buildListParams() });
      if (response.data?.status === true) {
        setRows(response.data.data || []);
        setTotal(response.data.total || 0);
        setTabCounts(response.data.tab_counts || { payroll: 0, infratel: 0, brms: 0, others: 0 });
        setSectionCounts(response.data.section_counts || { invoice: 0, payment: 0, uncoded: 0 });
        setSentTotalCount(response.data.sent_total || 0);
        setTrashTotalCount(response.data.trash_total || 0);
      }
    } catch (error) {
      // silent — next poll will retry
    }
  }, [buildListParams]);

  const [syncing, setSyncing] = useState(false);

  // A cron job (mail_cron.php, every 1-2 min) keeps the DB synced in the background now, so
  // pages just read — they don't trigger sync themselves. This button is only for "check right
  // now instead of waiting for the next cron tick" and deliberately does ONE batch (≤30, capped
  // server-side), not a loop — a big backlog is cron's job, not something a page load should
  // block on.
  const manualSync = async () => {
    try {
      setSyncing(true);
      const response = await axios.get(`${apiurl}mails.php`, { params: { mode: "sync" } });
      await fetchMails();
      if (response.data?.status === true) {
        const processed = response.data.processed || 0;
        const remaining = response.data.remaining || 0;
        Swal.fire({
          icon: "success",
          title: "Synced",
          text:
            processed > 0
              ? `${processed} new mail(s) fetched.${remaining ? ` ${remaining} more in the backlog — cron will pick those up shortly, or click again.` : ""}`
              : "No new mail — you're up to date.",
          timer: 2200,
          showConfirmButton: false,
        });
      } else {
        Swal.fire("Sync failed", response.data?.message || "Could not sync mails", "error");
      }
    } catch (error) {
      Swal.fire("Sync failed", "Could not reach the mail server", "error");
    } finally {
      setSyncing(false);
    }
  };

  const fetchSentMails = useCallback(async () => {
    try {
      setSentLoading(true);
      const params = { mode: "list_sent", type: activeTab, page: sentPage, page_size: pageSize };
      if (sentSearch.trim()) params.q = sentSearch.trim();
      const response = await axios.get(`${apiurl}mails.php`, { params });
      if (response.data?.status === true) {
        setSentRows(response.data.data || []);
        setSentTotal(response.data.total || 0);
        const counts = response.data.tab_counts || { payroll: 0, infratel: 0, brms: 0, others: 0 };
        setSentTabCounts(counts);
        setSentTotalCount(Object.values(counts).reduce((sum, n) => sum + n, 0));
      } else {
        setSentRows([]);
        setSentTotal(0);
      }
    } catch (error) {
      setSentRows([]);
      setSentTotal(0);
    } finally {
      setSentLoading(false);
    }
  }, [activeTab, sentPage, sentSearch]);

  const fetchTrashMails = useCallback(async () => {
    try {
      setTrashLoading(true);
      const params = { mode: "list_trash", page: trashPage, page_size: pageSize };
      if (trashSearch.trim()) params.q = trashSearch.trim();
      const response = await axios.get(`${apiurl}mails.php`, { params });
      if (response.data?.status === true) {
        setTrashRows(response.data.data || []);
        setTrashTotal(response.data.total || 0);
      } else {
        setTrashRows([]);
        setTrashTotal(0);
      }
    } catch (error) {
      setTrashRows([]);
      setTrashTotal(0);
    } finally {
      setTrashLoading(false);
    }
  }, [trashPage, trashSearch]);

  useEffect(() => {
    if (activeSection === "sent") {
      const timer = setTimeout(fetchSentMails, sentSearch ? 350 : 0);
      return () => clearTimeout(timer);
    }
    if (activeSection === "trash") {
      const timer = setTimeout(fetchTrashMails, trashSearch ? 350 : 0);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(fetchMails, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [activeSection, activeTab, fetchMails, fetchSentMails, fetchTrashMails, search, sentSearch, trashSearch]);


  useEffect(() => {
    const interval = setInterval(refreshSilently, 20000);
    return () => clearInterval(interval);
  }, [refreshSilently]);

  useEffect(() => {
    axios
      .get(`${apiurl}mails.php`, { params: { mode: "get_signature" } })
      .then((response) => {
        if (response.data?.status === true) {
          setSavedSignature(response.data.signature_html || null);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSignatureHtml = () =>
    `<p>Thanks and Regards,</p><p><strong>${currentUserName || currentUser || ""}</strong></p>`;

  const openSignatureModal = () => {
    setSignatureDraft(savedSignature ?? defaultSignatureHtml());
    setSignatureModalOpen(true);
  };

  const saveSignature = async () => {
    try {
      setSignatureSaving(true);
      const formData = new FormData();
      formData.append("mode", "save_signature");
      formData.append("signature_html", signatureDraft);
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status === true) {
        setSavedSignature(signatureDraft);
        setSignatureModalOpen(false);
        Swal.fire({ icon: "success", title: "Signature saved", timer: 1600, showConfirmButton: false });
      } else {
        Swal.fire("Error", response.data?.message || "Could not save signature", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Could not save signature", "error");
    } finally {
      setSignatureSaving(false);
    }
  };

  const clearFilters = () => {
    setSectionFilter("");
    setProgressFilter(progressFilterOptions[0]);
    setPriorityFilter(priorityFilterOptions[0]);
    setAssignedFilter({ value: "unassigned", label: "Unassigned" });
    setDateRangePreset(dateRangeOptions[0]);
    setCustomFrom("");
    setCustomTo("");
    setSearch("");
    setPage(1);
  };

  const selectFolder = (section, type) => {
    setActiveSection(section);
    setActiveTab(type);
    clearFilters();
    setSentSearch("");
    setSentPage(1);
    setSidebarOpen(false);
  };

  const selectTrash = () => {
    setActiveSection("trash");
    clearFilters();
    setTrashSearch("");
    setTrashPage(1);
    setSidebarOpen(false);
  };

  const assignedFilterOptions = [
    { value: "me", label: "Assigned to Me" },
    { value: "unassigned", label: "Unassigned" },
    // Own name excluded here — "Assigned to Me" already covers that, so listing it again
    // separately would just be a confusing duplicate.
    ...accountsStaff.filter((s) => s.username !== currentUser).map((s) => ({ value: s.username, label: s.name })),
    { value: "", label: "Everyone" },
  ];

  const typeBadgeClass = (type) => {
    if (type === "payroll") return "bg-info-transparent text-info";
    return "bg-secondary-transparent text-secondary";
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDatePart = (value) => {
    if (!value) return "";
    const d = new Date(value.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const formatTimePart = (value) => {
    if (!value) return "";
    const d = new Date(value.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  const progressColorClass = (progress) => {
    if (progress === "in_progress") return "mails-progress-in_progress";
    if (progress === "completed") return "mails-progress-completed";
    return "mails-progress-pending";
  };

  const priorityColorClass = (priority) => {
    if (priority === "high") return "mails-priority-high";
    if (priority === "low") return "mails-priority-low";
    return "mails-priority-medium";
  };

  const avatarPalette = ["#1e40af", "#0e7490", "#7c3aed", "#be185d", "#b45309", "#15803d", "#4338ca"];

  const getInitial = (name, email) => {
    const source = (name || email || "?").trim();
    return source.charAt(0).toUpperCase();
  };

  const getAvatarColor = (seed) => {
    const str = seed || "?";
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return avatarPalette[Math.abs(hash) % avatarPalette.length];
  };

  const resolveInlineImages = (html, mailUid) => {
    if (!html) return html;
    return html.replace(/cid:([^"')\s>]+)/gi, (match, cid) => {
      const decodedCid = decodeURIComponent(cid);
      return `${apiurl}mails.php?mode=inline_image&mail_uid=${mailUid}&cid=${encodeURIComponent(decodedCid)}`;
    });
  };

  const attachmentUrl = (id, download) =>
    `${apiurl}mails.php?mode=mail_attachment&id=${id}${download ? "&download=1" : ""}`;

  const invoiceFileUrl = (id, download) =>
    `${apiurl}mails.php?mode=invoice_file&id=${id}${download ? "&download=1" : ""}`;

  const replyAttachmentUrl = (id, download) =>
    `${apiurl}mails.php?mode=reply_attachment&id=${id}${download ? "&download=1" : ""}`;

  const previewFileUrl = (preview, download) => {
    if (preview.kind === "invoice") return invoiceFileUrl(preview.id, download);
    if (preview.kind === "reply_attachment") return replyAttachmentUrl(preview.id, download);
    return attachmentUrl(preview.id, download);
  };

  const handleDownload = (attachmentId, kind = "attachment") => {
    const url = kind === "reply_attachment" ? replyAttachmentUrl(attachmentId, true) : attachmentUrl(attachmentId, true);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openPreview = (kind, id, name) => {
    setPreviewMail({ kind, id, name });
  };

  const loadSheetPreview = useCallback(async (preview) => {
    const url = previewFileUrl(preview, false);
    try {
      setSheetLoading(true);
      setSheetError("");
      setSheetPreview(null);
      setSheetActiveTab(0);
      const response = await axios.get(url, { responseType: "arraybuffer" });
      const workbook = XLSX.read(response.data, { type: "array" });
      const sheets = workbook.SheetNames.map((name) => ({
        name,
        rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, blankrows: false }),
      }));
      setSheetPreview(sheets);
    } catch (error) {
      setSheetError("Could not parse this file for preview. Use Download instead.");
    } finally {
      setSheetLoading(false);
    }
  }, []);

  const assignStaff = async (row, targetUsername) => {
    try {
      setAssigningId(row.id);
      const formData = new FormData();
      formData.append("mode", "assign_staff");
      formData.append("mail_id", String(row.id));
      formData.append("target_username", targetUsername);
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status === true) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  assigned_to: response.data.assigned_to,
                  assigned_name: response.data.assigned_name,
                  assigned_at: response.data.assigned_at,
                  assignee_viewed_at: response.data.assignee_viewed_at,
                }
              : r
          )
        );
      } else {
        Swal.fire("Error", response.data?.message || "Could not reassign", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Could not reassign", "error");
    } finally {
      setAssigningId(null);
    }
  };

  // Every accounts user can now assign/reassign/unassign any mail (not just the accounts
  // head) — a native <select> changes its displayed value the instant you pick an option, so
  // this confirms first and only calls assignStaff on "Yes"; on cancel the <select> just
  // re-renders back to row.assigned_to since it's a controlled value, no manual revert needed.
  const confirmAssignStaff = async (row, targetUsername, targetLabel) => {
    const confirm = await Swal.fire({
      title: targetUsername ? `Assign to ${targetLabel}?` : "Unassign this mail?",
      text: targetUsername
        ? "This changes who's responsible for this mail."
        : "This mail will go back to being unassigned.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: targetUsername ? "Yes, assign" : "Yes, unassign",
    });
    if (!confirm.isConfirmed) return;
    assignStaff(row, targetUsername);
  };

  const deleteMail = async (row) => {
    const confirm = await Swal.fire({
      title: "Move this mail to Trash?",
      text: "You can find it in the Trash folder afterwards.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, move to Trash",
    });
    if (!confirm.isConfirmed) return;

    try {
      const formData = new FormData();
      formData.append("mode", "delete_mail");
      formData.append("mail_id", String(row.id));
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status === true) {
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        setTabCounts((prev) => ({ ...prev, [row.type]: Math.max(0, (prev[row.type] || 0) - 1) }));
        setTrashTotalCount((prev) => prev + 1);
      } else {
        Swal.fire("Error", response.data?.message || "Could not move mail to Trash", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Could not move mail to Trash", "error");
    }
  };

  const restoreMail = async (row) => {
    const confirm = await Swal.fire({
      title: "Restore this mail?",
      text: "It'll go back to its original folder in the Inbox.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, restore",
    });
    if (!confirm.isConfirmed) return;

    try {
      const formData = new FormData();
      formData.append("mode", "restore_mail");
      formData.append("mail_id", String(row.id));
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status === true) {
        setTrashRows((prev) => prev.filter((r) => r.id !== row.id));
        setTrashTotalCount((prev) => Math.max(0, prev - 1));
      } else {
        Swal.fire("Error", response.data?.message || "Could not restore mail", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Could not restore mail", "error");
    }
  };

  const updateProgress = async (row, progress) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, progress } : r)));
    try {
      const formData = new FormData();
      formData.append("mode", "update_progress");
      formData.append("mail_id", String(row.id));
      formData.append("progress", progress);
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status !== true) {
        Swal.fire("Error", response.data?.message || "Could not update progress", "error");
        refreshSilently();
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Could not update progress", "error");
      refreshSilently();
    }
  };

  const updatePriority = async (row, priority) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, priority } : r)));
    try {
      const formData = new FormData();
      formData.append("mode", "update_priority");
      formData.append("mail_id", String(row.id));
      formData.append("priority", priority);
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status !== true) {
        Swal.fire("Error", response.data?.message || "Could not update priority", "error");
        refreshSilently();
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Could not update priority", "error");
      refreshSilently();
    }
  };

  // Own inbox appears under more than one historical alias in raw mail headers
  // (e.g. an old typo'd domain from before a migration) — exclude all known ones,
  // not just the currently configured address, or stale addresses leak into Cc.
  const OWN_MAILBOX_ALIASES = ["accounts.karam@meraqui.com", "accounts.karam@meraquii.in"];

  // Overview headers come as raw RFC822 strings (e.g. `"Jane Doe" <jane@x.com>, john@y.com`),
  // not clean lists — pulling every email-shaped substring out is more robust than splitting
  // on commas, since display names can themselves contain commas/quotes.
  const parseEmailList = (value) => (value || "").match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g) || [];

  // Lots of mail in this inbox arrives as a manually-forwarded chain (Outlook pastes
  // "From/Sent/To/Cc:" as plain text into the body) rather than real headers — the actual
  // envelope only ever lists our shared inbox. Mining those quoted To:/Cc: lines out of the
  // body is the only way to recover who was really on the thread.
  const extractQuotedRecipients = (mail) => {
    const raw = mail.body_html
      ? mail.body_html
          .replace(/<style[\s\S]*?<\/style>/gi, " ")
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<\/(p|div|tr|li)>/gi, "\n")
          .replace(/<[^>]+>/g, " ")
      : mail.body_text || "";
    const decoded = raw.replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&");
    const emails = [];
    // A pasted "To:"/"Cc:" value often line-wraps mid-list (Outlook wraps long recipient
    // lines), so a strict per-line match misses names past the wrap. Instead capture
    // everything up to the next header label (From/Sent/To/Cc/Subject) or a blank line,
    // which correctly spans wrapped lines while still stopping at the next real field.
    const headerBlockRegex = /(?:^|\n)\s*(?:to|cc)\s*:\s*([\s\S]*?)(?=\n\s*(?:from|sent|to|cc|subject)\s*:|\n\s*\n|$)/gi;
    let match;
    while ((match = headerBlockRegex.exec(decoded)) !== null) {
      emails.push(...parseEmailList(match[1]));
    }
    return emails;
  };

  const buildReplyAllCc = (mail) => {
    const own = new Set(
      [ownMailbox, ...OWN_MAILBOX_ALIASES].filter(Boolean).map((a) => a.toLowerCase())
    );
    const from = (mail.from_email || "").toLowerCase();
    const combined = [
      ...parseEmailList(mail.to_email),
      ...parseEmailList(mail.cc_email),
      ...extractQuotedRecipients(mail),
    ];
    const seen = new Set();
    const cc = [];
    combined.forEach((addr) => {
      const key = addr.toLowerCase();
      if (own.has(key) || key === from || seen.has(key)) return;
      seen.add(key);
      cc.push(addr);
    });
    return cc.join(", ");
  };

  const openReplyWithMail = (mail, mode = "reply") => {
    const subject = mail.subject || "";
    setReplyTarget(mail);
    setReplySubject(/^re:/i.test(subject.trim()) ? subject : `Re: ${subject}`);
    setReplyCc(mode === "reply_all" ? buildReplyAllCc(mail) : "");
    setReplyBody("");
    setReplyAttachments([]);
    setShowQuoted(true);
  };

  const openReplyForRow = async (row, mode = "reply") => {
    try {
      setReplyLoadingId(row.id);
      const response = await axios.get(`${apiurl}mails.php`, { params: { mode: "detail", id: row.id } });
      if (response.data?.status === true) {
        openReplyWithMail(response.data.data, mode);
      } else {
        Swal.fire("Error", "Could not load this mail", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Could not load this mail", "error");
    } finally {
      setReplyLoadingId(null);
    }
  };

  const closeReply = () => {
    setReplyTarget(null);
    setReplySubject("");
    setReplyCc("");
    setReplyBody("");
    setReplyAttachments([]);
    setSignaturePreviewExpanded(false);
  };

  const [copyingId, setCopyingId] = useState(null);

  // Browsers don't let a webpage place a real file on the OS clipboard — only text/HTML/image
  // bytes. So "Copy Mail" copies the subject/from/date/body as rich text (pastes cleanly into
  // Outlook, Gmail, Word) but attachments can never travel through it; those still need Download.
  const copyMailToClipboard = async (mail) => {
    const fromLine = mail.from_name ? `${mail.from_name} <${mail.from_email}>` : mail.from_email;
    const plainBody = mail.body_text || (mail.body_html || "").replace(/<[^>]+>/g, " ");
    const plain = `From: ${fromLine}\nDate: ${formatDateTime(mail.received_at)}\nSubject: ${mail.subject || ""}\n\n${plainBody}`;
    const htmlBody = mail.body_html || `<pre>${(mail.body_text || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))}</pre>`;
    const html =
      `<div><p><b>From:</b> ${fromLine.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]))}` +
      `<br><b>Date:</b> ${formatDateTime(mail.received_at)}<br><b>Subject:</b> ${mail.subject || ""}</p><hr>${htmlBody}</div>`;

    try {
      if (navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      const hasAttachments = (mail.attachments && mail.attachments.length > 0) || mail.invoice_file_name;
      Swal.fire({
        icon: "success",
        title: "Mail copied",
        text: hasAttachments
          ? "Paste into Outlook/Gmail/Word. Attachments aren't included — download those separately."
          : "Paste into Outlook, Gmail, or Word.",
        timer: 2600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire("Error", "Could not copy — your browser may be blocking clipboard access", "error");
    }
  };

  const copyMailForRow = async (row) => {
    try {
      setCopyingId(row.id);
      const response = await axios.get(`${apiurl}mails.php`, { params: { mode: "detail", id: row.id } });
      if (response.data?.status === true) {
        await copyMailToClipboard(response.data.data);
      } else {
        Swal.fire("Error", "Could not load this mail", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Could not load this mail", "error");
    } finally {
      setCopyingId(null);
    }
  };

  const downloadMailZip = (id) => {
    window.open(`${apiurl}mails.php?mode=download_zip&id=${id}`, "_blank", "noopener,noreferrer");
  };

  const handleReplyBodyPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) pastedFiles.push(file);
      }
    }
    if (pastedFiles.length === 0) return;
    // A copied image/file (e.g. from Outlook, a screenshot tool, or Finder) has no place in a
    // plain-text reply body, so treat it the same as picking it via the Attach button instead
    // of letting the browser drop a broken placeholder into the textarea.
    e.preventDefault();
    const named = pastedFiles.map((file, idx) => {
      if (file.name && !/^image\.(png|jpe?g|gif|webp)$/i.test(file.name)) return file;
      const ext = (file.type.split("/")[1] || "png").split("+")[0];
      return new File([file], `pasted-${Date.now()}-${idx}.${ext}`, { type: file.type });
    });
    setReplyAttachments((prev) => [...prev, ...named]);
  };

  const handleReplyAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    setReplyAttachments((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeReplyAttachment = (index) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const sendReply = async () => {
    if (!replyTarget) return;
    if (!replyBody.trim() && replyAttachments.length === 0) {
      Swal.fire("Error", "Reply cannot be empty", "error");
      return;
    }
    try {
      setSendingReply(true);
      const formData = new FormData();
      formData.append("mode", "send_reply");
      formData.append("mail_id", String(replyTarget.id));
      formData.append("body", replyBody);
      formData.append("cc", replyCc);
      replyAttachments.forEach((file) => formData.append("attachments[]", file));
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status === true) {
        Swal.fire("Sent", "Reply sent successfully", "success");
        if (detailMail && detailMail.id === replyTarget.id) {
          setDetailMail((prev) => ({
            ...prev,
            replies: [...(prev.replies || []), response.data.reply],
          }));
        }
        closeReply();
      } else {
        Swal.fire("Error", response.data?.message || "Could not send reply", "error");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Could not send reply", "error");
    } finally {
      setSendingReply(false);
    }
  };

  const openMailDetail = (id) => {
    setDetailMailId(id);
  };

  const closeMailDetail = () => {
    setDetailMailId(null);
    setDetailMail(null);
  };

  useEffect(() => {
    if (!detailMailId) return;
    (async () => {
      try {
        setDetailLoading(true);
        setDetailMail(null);
        const response = await axios.get(`${apiurl}mails.php`, { params: { mode: "detail", id: detailMailId } });
        if (response.data?.status === true) {
          setDetailMail(response.data.data);
          // Opening the mail is what clears its red-dot server-side (see mode=detail) — mirror
          // that into the already-loaded row list so the dot disappears without a full refetch.
          const viewedAt = response.data.data.assignee_viewed_at;
          if (viewedAt) {
            setRows((prev) => prev.map((r) => (r.id === detailMailId ? { ...r, assignee_viewed_at: viewedAt } : r)));
          }
        }
      } catch (error) {
        Swal.fire("Error", "Could not load the mail", "error");
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [detailMailId]);

  useEffect(() => {
    if (!replyCcRef.current) return;
    replyCcRef.current.style.height = "auto";
    replyCcRef.current.style.height = `${replyCcRef.current.scrollHeight}px`;
  }, [replyCc, replyTarget]);

  useEffect(() => {
    if (!previewMail) return;
    if (isSpreadsheetPreviewable(previewMail.name)) {
      loadSheetPreview(previewMail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMail]);

  const columnCount = assignedFilter?.value === "me" ? 11 : 9;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const sentTotalPages = Math.max(1, Math.ceil(sentTotal / pageSize));

  const getPageButtons = (currentPage, lastPage) => {
    if (lastPage <= 1) return [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(lastPage, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    const pages = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  };

  return (
    <div className="main-content app-content mails-page">
      <div className="container-fluid mt-4">
        <div className="mails-toolbar-row">
          <button
            type="button"
            className="mails-hamburger-btn"
            title={sidebarOpen ? "Hide folders" : "Show folders"}
            onClick={() => setSidebarOpen((o) => !o)}
          >
            <i className={sidebarOpen ? "ri-close-line" : "ri-menu-line"} />
          </button>
          <h5 className="mails-page-title mb-0">
            {activeSection === "trash"
              ? "Trash"
              : `${activeSection === "sent" ? "Sent" : "Inbox"} / ${folderTypes.find((f) => f.key === activeTab)?.label || ""}`}
          </h5>
          {activeSection === "inbox" && (
            <div className="mails-type-toggle">
              {(sectionToggleOptionsByTab[activeTab] || sectionToggleOptionsByTab.payroll).map((opt) => {
                const toggleCount = opt.value
                  ? sectionCounts[opt.value] || 0
                  : (sectionCounts.invoice || 0) + (sectionCounts.payment || 0) + (sectionCounts.uncoded || 0);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`mails-type-toggle-btn ${sectionFilter === opt.value ? "active" : ""}`}
                    onClick={() => {
                      setSectionFilter(opt.value);
                      setPage(1);
                    }}
                  >
                    {opt.label} <span className="mails-toggle-count">{toggleCount}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="d-flex gap-2 mails-toolbar-actions">
            <button type="button" className="mails-sync-btn mails-signature-btn" onClick={openSignatureModal}>
              <EditNoteRoundedIcon style={{ fontSize: "18px" }} />
              <span>Signature</span>
            </button>
            <button type="button" className="mails-sync-btn" disabled={syncing} onClick={manualSync}>
              <RefreshRoundedIcon className={syncing ? "mails-sync-spin" : ""} style={{ fontSize: "18px" }} />
              <span>{syncing ? "Fetching latest mails..." : "Fetch Latest Mails"}</span>
            </button>
          </div>
        </div>

        <div className="mails-layout">
          <div
            className={`mails-sidebar-backdrop ${sidebarOpen ? "open" : ""}`}
            onClick={() => setSidebarOpen(false)}
          />
          <div className={`mails-sidebar ${sidebarOpen ? "open" : ""}`}>
              <div className="mails-sidebar-topbar">
                <span className="mails-sidebar-topbar-title">Mails</span>
                <button
                  type="button"
                  className="mails-sidebar-close-btn"
                  title="Hide folders"
                  onClick={() => setSidebarOpen(false)}
                >
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="mails-sidebar-section">
                <div className="mails-sidebar-heading">
                  <i className="ri-inbox-line" /> Inbox
                  <span className="mails-sidebar-heading-count">
                    {Object.values(tabCounts).reduce((sum, n) => sum + n, 0)}
                  </span>
                </div>
                {folderTypes.map((folder) => (
                  <button
                    key={`inbox-${folder.key}`}
                    type="button"
                    className={`mails-sidebar-item ${activeSection === "inbox" && activeTab === folder.key ? "active" : ""}`}
                    onClick={() => selectFolder("inbox", folder.key)}
                  >
                    <i className={folder.icon} />
                    <span>{folder.label}</span>
                    <span className="mails-sidebar-count">{tabCounts[folder.key] || 0}</span>
                  </button>
                ))}
              </div>
              <div className="mails-sidebar-section">
                <div className="mails-sidebar-heading">
                  <i className="ri-send-plane-line" /> Sent
                  <span className="mails-sidebar-heading-count">{sentTotalCount}</span>
                </div>
                {folderTypes.map((folder) => (
                  <button
                    key={`sent-${folder.key}`}
                    type="button"
                    className={`mails-sidebar-item ${activeSection === "sent" && activeTab === folder.key ? "active" : ""}`}
                    onClick={() => selectFolder("sent", folder.key)}
                  >
                    <i className={folder.icon} />
                    <span>{folder.label}</span>
                    <span className="mails-sidebar-count">{sentTabCounts[folder.key] || 0}</span>
                  </button>
                ))}
              </div>
              <div className="mails-sidebar-section">
                <button
                  type="button"
                  className={`mails-sidebar-item ${activeSection === "trash" ? "active" : ""}`}
                  onClick={selectTrash}
                >
                  <i className="ri-delete-bin-line" />
                  <span>Trash</span>
                  <span className="mails-sidebar-count">{trashTotalCount}</span>
                </button>
              </div>
          </div>

          <div className="mails-layout-content">

        {activeSection === "sent" ? (
          <>
            <div className="advance-payment-panel mb-3 mails-filters-panel">
              <div className="mails-filters-row">
                <form
                  className="d-flex mails-filter-search"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setSentPage(1);
                  }}
                >
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by subject or recipient"
                    value={sentSearch}
                    onChange={(e) => {
                      setSentSearch(e.target.value);
                      setSentPage(1);
                    }}
                    style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  />
                  {sentSearch ? (
                    <button
                      type="button"
                      className="btn btn-light"
                      style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
                      onClick={() => {
                        setSentSearch("");
                        setSentPage(1);
                      }}
                      title="Clear search"
                    >
                      <i className="ri-close-line" />
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    className="advance-payment-primary-btn"
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                  >
                    <i className="ri-search-line" /> Search
                  </button>
                </form>
              </div>
            </div>

            <div className="advance-payment-table table-responsive mails-table">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "50px" }}>S.No</th>
                    <th>To</th>
                    <th>Subject</th>
                    <th style={{ width: "160px" }}>Sent By</th>
                    <th style={{ width: "150px" }}>Sent At</th>
                    <th className="text-center" style={{ width: "130px" }}>Attachment</th>
                    <th className="text-center" style={{ width: "90px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sentLoading ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Loading...
                      </td>
                    </tr>
                  ) : sentRows.length > 0 ? (
                    sentRows.map((row, index) => (
                      <tr key={row.id} className="mails-clickable-row" onClick={() => openMailDetail(row.mail_id)}>
                        <td>{index + 1}</td>
                        <td>{row.to_email}</td>
                        <td className="mails-subject-cell" title={row.subject}>
                          {row.subject}
                        </td>
                        <td>{row.sent_by_name || row.sent_by}</td>
                        <td>
                          <div>{formatDatePart(row.sent_at)}</div>
                          <div className="mails-received-time">{formatTimePart(row.sent_at)}</div>
                        </td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {row.attachments && row.attachments.length > 0 ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                row.attachments.length === 1
                                  ? openPreview("reply_attachment", row.attachments[0].id, row.attachments[0].file_name)
                                  : setAttachmentsListRow({ attachments: row.attachments, isReply: true })
                              }
                            >
                              View{row.attachments.length > 1 ? ` (${row.attachments.length})` : ""}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="btn btn-sm btn-light mails-view-btn"
                            title="Open original mail"
                            onClick={() => openMailDetail(row.mail_id)}
                          >
                            <VisibilityRoundedIcon style={{ fontSize: "18px" }} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="border-0">
                        <div className="mails-empty-state text-center py-5">
                          <img
                            src={process.env.PUBLIC_URL + "/images/media/no-data.svg"}
                            alt="No Data"
                            className="d-block mx-auto"
                          />
                          <p className="text-muted mb-0">No sent mails yet</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted">
                {sentTotal > 0
                  ? `Showing ${(sentPage - 1) * pageSize + 1} to ${Math.min(sentPage * pageSize, sentTotal)} of ${sentTotal}`
                  : "No records"}
              </div>
              <div className="d-flex align-items-center gap-1">
                <button type="button" className="btn btn-light px-3 py-2" disabled={sentPage <= 1 || sentLoading} onClick={() => setSentPage(1)}>
                  First
                </button>
                <button
                  type="button"
                  className="btn btn-light px-3 py-2"
                  disabled={sentPage <= 1 || sentLoading}
                  onClick={() => setSentPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                {getPageButtons(sentPage, sentTotalPages).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`btn px-3 py-2 ${p === sentPage ? "btn-primary" : "btn-light"}`}
                    disabled={sentLoading}
                    onClick={() => setSentPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-light px-3 py-2"
                  disabled={sentPage >= sentTotalPages || sentLoading}
                  onClick={() => setSentPage((p) => Math.min(sentTotalPages, p + 1))}
                >
                  Next
                </button>
                <button
                  type="button"
                  className="btn btn-light px-3 py-2"
                  disabled={sentPage >= sentTotalPages || sentLoading}
                  onClick={() => setSentPage(sentTotalPages)}
                >
                  Last
                </button>
              </div>
            </div>
          </>
        ) : activeSection === "trash" ? (
          <>
            <div className="advance-payment-panel mb-3 mails-filters-panel">
              <div className="mails-filters-row">
                <div className="mails-filter-group mails-filter-search-group">
                  <label className="mails-filter-label">Search</label>
                  <form
                    className="d-flex mails-filter-search"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setTrashPage(1);
                    }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search..."
                      title="Search by subject or sender"
                      value={trashSearch}
                      onChange={(e) => {
                        setTrashSearch(e.target.value);
                        setTrashPage(1);
                      }}
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    />
                    {trashSearch ? (
                      <button
                        type="button"
                        className="btn btn-light"
                        style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
                        onClick={() => {
                          setTrashSearch("");
                          setTrashPage(1);
                        }}
                        title="Clear search"
                      >
                        <i className="ri-close-line" />
                      </button>
                    ) : null}
                    <button
                      type="submit"
                      className="advance-payment-primary-btn"
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    >
                      <i className="ri-search-line" /> Search
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div className="advance-payment-table table-responsive mails-table">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "50px" }}>S.No</th>
                    <th>Subject</th>
                    <th>From</th>
                    <th style={{ width: "110px" }}>Type</th>
                    <th style={{ width: "150px" }}>Deleted At</th>
                    <th className="text-center" style={{ width: "100px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {trashLoading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Loading...
                      </td>
                    </tr>
                  ) : trashRows.length > 0 ? (
                    trashRows.map((row, index) => (
                      <tr key={row.id} className="mails-clickable-row" onClick={() => openMailDetail(row.id)}>
                        <td>{index + 1}</td>
                        <td className="mails-subject-cell" title={row.subject}>
                          {row.subject}
                        </td>
                        <td>{row.from_name ? `${row.from_name} <${row.from_email}>` : row.from_email}</td>
                        <td>
                          <span className={`badge ${typeBadgeClass(row.type)} px-3 py-2`}>
                            {row.type === "payroll" ? "Payroll" : "Others"}
                          </span>
                        </td>
                        <td>{formatDateTime(row.deleted_at)}</td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="btn btn-sm mails-reply-btn"
                            title="Restore"
                            onClick={() => restoreMail(row)}
                          >
                            <RestoreFromTrashRoundedIcon style={{ fontSize: "16px" }} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="border-0">
                        <div className="mails-empty-state text-center py-5">
                          <img
                            src={process.env.PUBLIC_URL + "/images/media/no-data.svg"}
                            alt="No Data"
                            className="d-block mx-auto"
                          />
                          <p className="text-muted mb-0">Trash is empty</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted">
                {trashTotal > 0
                  ? `Showing ${(trashPage - 1) * pageSize + 1} to ${Math.min(trashPage * pageSize, trashTotal)} of ${trashTotal}`
                  : "No records"}
              </div>
              <div className="d-flex align-items-center gap-1">
                <button type="button" className="btn btn-light px-3 py-2" disabled={trashPage <= 1 || trashLoading} onClick={() => setTrashPage(1)}>
                  First
                </button>
                <button
                  type="button"
                  className="btn btn-light px-3 py-2"
                  disabled={trashPage <= 1 || trashLoading}
                  onClick={() => setTrashPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                {getPageButtons(trashPage, Math.max(1, Math.ceil(trashTotal / pageSize))).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`btn px-3 py-2 ${p === trashPage ? "btn-primary" : "btn-light"}`}
                    disabled={trashLoading}
                    onClick={() => setTrashPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  className="btn btn-light px-3 py-2"
                  disabled={trashPage >= Math.max(1, Math.ceil(trashTotal / pageSize)) || trashLoading}
                  onClick={() => setTrashPage((p) => Math.min(Math.max(1, Math.ceil(trashTotal / pageSize)), p + 1))}
                >
                  Next
                </button>
                <button
                  type="button"
                  className="btn btn-light px-3 py-2"
                  disabled={trashPage >= Math.max(1, Math.ceil(trashTotal / pageSize)) || trashLoading}
                  onClick={() => setTrashPage(Math.max(1, Math.ceil(trashTotal / pageSize)))}
                >
                  Last
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
        <div className="advance-payment-panel mb-3 mails-filters-panel">
          <div className="mails-filters-row">
            <div className="mails-filter-group">
              <label className="mails-filter-label">Assigned</label>
              <Select
                className="mails-filter-select"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                options={assignedFilterOptions}
                value={assignedFilter}
                onChange={(opt) => {
                  setAssignedFilter(opt);
                  // Progress/Priority only make sense (and only render) once you're looking at
                  // your own assigned mail — reset them so a stale hidden filter doesn't keep
                  // silently narrowing the list after you switch away from "Assigned to Me".
                  if (opt?.value !== "me") {
                    setProgressFilter(progressFilterOptions[0]);
                    setPriorityFilter(priorityFilterOptions[0]);
                  }
                  setPage(1);
                }}
                isSearchable={false}
              />
            </div>
            {assignedFilter?.value === "me" && (
              <>
                <div className="mails-filter-group">
                  <label className="mails-filter-label">Progress</label>
                  <Select
                    className="mails-filter-select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    options={progressFilterOptions}
                    value={progressFilter}
                    onChange={(opt) => {
                      setProgressFilter(opt);
                      setPage(1);
                    }}
                    isSearchable={false}
                  />
                </div>
                <div className="mails-filter-group">
                  <label className="mails-filter-label">Priority</label>
                  <Select
                    className="mails-filter-select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    options={priorityFilterOptions}
                    value={priorityFilter}
                    onChange={(opt) => {
                      setPriorityFilter(opt);
                      setPage(1);
                    }}
                    isSearchable={false}
                  />
                </div>
              </>
            )}
            <div className="mails-filter-group">
              <label className="mails-filter-label">Date Range</label>
              <Select
                className="mails-filter-select"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                options={dateRangeOptions}
                value={dateRangePreset}
                onChange={(opt) => {
                  setDateRangePreset(opt);
                  setPage(1);
                }}
                isSearchable={false}
              />
            </div>
            {dateRangePreset?.value === "custom" && (
              <>
                <div className="mails-filter-group">
                  <label className="mails-filter-label">From</label>
                  <input
                    type="date"
                    className="form-control mails-filter-date"
                    value={customFrom}
                    onChange={(e) => {
                      setCustomFrom(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="mails-filter-group">
                  <label className="mails-filter-label">To</label>
                  <input
                    type="date"
                    className="form-control mails-filter-date"
                    value={customTo}
                    onChange={(e) => {
                      setCustomTo(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </>
            )}
            <div className="mails-filter-group">
              <label className="mails-filter-label">&nbsp;</label>
              <button type="button" className="btn btn-light" onClick={clearFilters}>
                Clear
              </button>
            </div>
            <div className="mails-filter-group mails-filter-search-group">
              <label className="mails-filter-label">Search</label>
              <form
                className="d-flex mails-filter-search"
                onSubmit={(e) => {
                  e.preventDefault();
                  setPage(1);
                }}
              >
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search..."
                  title="Search by subject or sender"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                />
                {search ? (
                  <button
                    type="button"
                    className="btn btn-light"
                    style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
                    onClick={() => {
                      setSearch("");
                      setPage(1);
                    }}
                    title="Clear search"
                  >
                    <i className="ri-close-line" />
                  </button>
                ) : null}
                <button
                  type="submit"
                  className="advance-payment-primary-btn"
                  style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                >
                  <i className="ri-search-line" /> Search
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="advance-payment-table table-responsive mails-table">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th style={{ width: "50px" }}></th>
                <th style={{ width: "50px" }}>S.No</th>
                <th className="text-center" style={{ width: "200px" }}>Assigned</th>
                {assignedFilter?.value === "me" && (
                  <>
                    <th style={{ width: "200px" }}>Progress</th>
                    <th style={{ width: "130px" }}>Priority</th>
                  </>
                )}
                <th>Subject</th>
                <th>From</th>
                <th style={{ width: "110px" }}>Type</th>
                <th style={{ width: "150px" }}>Received At</th>
                <th className="text-center" style={{ width: "130px" }}>Attachment</th>
                <th className="text-center" style={{ width: "215px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="text-center text-muted py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Loading...
                  </td>
                </tr>
              ) : rows.length > 0 ? (
                rows.map((row, index) => {
                  const isMine = row.assigned_to && row.assigned_to === currentUser;
                  const isUnassigned = !row.assigned_to;
                  // Red dot means "you haven't looked at this yet" — unassigned mail always
                  // qualifies; assigned mail only qualifies for the person it's assigned to,
                  // and stays lit until they actually open it (reassigning doesn't clear it).
                  const isUnseenByMe = isMine && !row.assignee_viewed_at;
                  const isNew = isUnassigned || isUnseenByMe;
                  return (
                    <tr
                      key={row.id}
                      className={`mails-clickable-row ${isNew ? "mails-row-unassigned" : ""}`}
                      onClick={() => openMailDetail(row.id)}
                    >
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-sm btn-light mails-view-btn"
                          title={isUnassigned ? "New — not yet assigned" : isUnseenByMe ? "New — assigned to you" : "View full mail"}
                          onClick={() => openMailDetail(row.id)}
                        >
                          <VisibilityRoundedIcon style={{ fontSize: "18px" }} />
                          {isNew && <span className="mails-new-dot"></span>}
                        </button>
                      </td>
                      <td>{index + 1}</td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div>
                          <select
                            className="form-select form-select-sm"
                            value={row.assigned_to || ""}
                            disabled={assigningId === row.id}
                            onChange={(e) => {
                              const targetUsername = e.target.value;
                              const targetLabel = accountsStaff.find((s) => s.username === targetUsername)?.name || "";
                              confirmAssignStaff(row, targetUsername, targetLabel);
                            }}
                          >
                            <option value="">Unassigned</option>
                            {accountsStaff.map((s) => (
                              <option key={s.username} value={s.username}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                          {row.assigned_to && (
                            <div className="mails-assigned-at">{formatDateTime(row.assigned_at)}</div>
                          )}
                        </div>
                      </td>
                      {assignedFilter?.value === "me" && (
                        <>
                          <td onClick={(e) => e.stopPropagation()}>
                            <select
                              className={`form-select form-select-sm ${progressColorClass(row.progress || "pending")}`}
                              value={row.progress || "pending"}
                              disabled={!isAccountsHead && !isMine}
                              title={!isAccountsHead && !isMine ? "Only the assignee or accounts head can update progress" : ""}
                              onChange={(e) => updateProgress(row, e.target.value)}
                            >
                              {progressOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <select
                              className={`form-select form-select-sm ${priorityColorClass(row.priority || "medium")}`}
                              value={row.priority || "medium"}
                              disabled={!isAccountsHead && !isMine}
                              title={!isAccountsHead && !isMine ? "Only the assignee or accounts head can set priority" : ""}
                              onChange={(e) => updatePriority(row, e.target.value)}
                            >
                              {priorityOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        </>
                      )}
                      <td className="mails-subject-cell" title={row.subject}>{row.subject}</td>
                      <td>{row.from_name ? `${row.from_name} <${row.from_email}>` : row.from_email}</td>
                      <td>
                        <span className={`badge ${typeBadgeClass(row.type)} px-3 py-2`}>
                          {row.type === "payroll" ? "Payroll" : "Others"}
                        </span>
                      </td>
                      <td>
                        <div>{formatDatePart(row.received_at)}</div>
                        <div className="mails-received-time">{formatTimePart(row.received_at)}</div>
                      </td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        {row.attachments && row.attachments.length > 0 ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              row.attachments.length === 1
                                ? openPreview("attachment", row.attachments[0].id, row.attachments[0].file_name)
                                : setAttachmentsListRow(row)
                            }
                          >
                            View{row.attachments.length > 1 ? ` (${row.attachments.length})` : ""}
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex gap-1 justify-content-center">
                          <button
                            type="button"
                            className="btn btn-sm mails-reply-btn"
                            disabled={replyLoadingId === row.id}
                            title="Reply"
                            onClick={() => openReplyForRow(row, "reply")}
                          >
                            <ReplyRoundedIcon style={{ fontSize: "16px" }} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm mails-reply-btn"
                            disabled={replyLoadingId === row.id}
                            title="Reply All"
                            onClick={() => openReplyForRow(row, "reply_all")}
                          >
                            <ReplyAllRoundedIcon style={{ fontSize: "16px" }} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm mails-reply-btn"
                            disabled={copyingId === row.id}
                            title="Copy Mail"
                            onClick={() => copyMailForRow(row)}
                          >
                            <ContentCopyRoundedIcon style={{ fontSize: "16px" }} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm mails-reply-btn"
                            title="Download Mail (with attachments, as .zip)"
                            onClick={() => downloadMailZip(row.id)}
                          >
                            <DownloadRoundedIcon style={{ fontSize: "16px" }} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm mails-reply-btn"
                            title="Move to Trash"
                            onClick={() => deleteMail(row)}
                          >
                            <DeleteOutlineRoundedIcon style={{ fontSize: "16px" }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columnCount} className="border-0">
                    <div className="mails-empty-state text-center py-5">
                      <img
                        src={process.env.PUBLIC_URL + "/images/media/no-data.svg"}
                        alt="No Data"
                        className="d-block mx-auto"
                      />
                      <p className="text-muted mb-0">No mails found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <div className="text-muted">
            {total > 0 ? `Showing ${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, total)} of ${total}` : "No records"}
          </div>
          <div className="d-flex align-items-center gap-1">
            <button type="button" className="btn btn-light px-3 py-2" disabled={page <= 1 || loading} onClick={() => setPage(1)}>
              First
            </button>
            <button
              type="button"
              className="btn btn-light px-3 py-2"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            {getPageButtons(page, totalPages).map((p) => (
              <button
                key={p}
                type="button"
                className={`btn px-3 py-2 ${p === page ? "btn-primary" : "btn-light"}`}
                disabled={loading}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              className="btn btn-light px-3 py-2"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
            <button
              type="button"
              className="btn btn-light px-3 py-2"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(totalPages)}
            >
              Last
            </button>
          </div>
        </div>
          </>
        )}
          </div>
        </div>
      </div>

      {previewMail && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setPreviewMail(null)}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate d-flex align-items-center gap-2">
                  {fileIcon(previewMail.name, "mails-attachment-icon")}
                  {previewMail.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setPreviewMail(null);
                    setSheetPreview(null);
                    setSheetError("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {(() => {
                  const fileName = previewMail.name;

                  if (isInlinePreviewable(fileName)) {
                    return (
                      <iframe
                        className="mails-preview-iframe"
                        src={previewFileUrl(previewMail, false)}
                        title="attachment-preview"
                      />
                    );
                  }

                  if (isSpreadsheetPreviewable(fileName)) {
                    if (sheetLoading) {
                      return (
                        <div className="text-center py-5 text-muted">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Parsing spreadsheet...
                        </div>
                      );
                    }
                    if (sheetError) {
                      return <div className="text-muted text-center py-5">{sheetError}</div>;
                    }
                    if (sheetPreview && sheetPreview.length > 0) {
                      const activeSheet = sheetPreview[sheetActiveTab] || sheetPreview[0];
                      return (
                        <div>
                          {sheetPreview.length > 1 && (
                            <div className="d-flex gap-2 mb-2 flex-wrap">
                              {sheetPreview.map((sheet, idx) => (
                                <button
                                  key={sheet.name}
                                  type="button"
                                  className={`btn btn-sm ${idx === sheetActiveTab ? "btn-primary" : "btn-outline-secondary"}`}
                                  onClick={() => setSheetActiveTab(idx)}
                                >
                                  {sheet.name}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="mails-sheet-preview">
                            <table className="table table-bordered table-sm mb-0">
                              <tbody>
                                {activeSheet.rows.map((row, rIdx) => (
                                  <tr key={rIdx}>
                                    {row.map((cell, cIdx) => (
                                      <td key={cIdx}>{cell !== undefined && cell !== null ? String(cell) : ""}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    }
                  }

                  return (
                    <div className="text-muted text-center py-5">
                      Preview isn't available for this file type. Use Download instead.
                    </div>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => window.open(previewFileUrl(previewMail, true), "_blank", "noopener,noreferrer")}
                >
                  Download
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setPreviewMail(null);
                    setSheetPreview(null);
                    setSheetError("");
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {attachmentsListRow && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setAttachmentsListRow(null)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate">Attachments</h5>
                <button type="button" className="btn-close" onClick={() => setAttachmentsListRow(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mails-detail-attachments mails-detail-attachments-lg p-0 border-0">
                  {attachmentsListRow.attachments.map((att) => (
                    <AttachmentTile
                      key={att.id}
                      name={att.file_name}
                      large
                      onView={() => {
                        setAttachmentsListRow(null);
                        openPreview(attachmentsListRow.isReply ? "reply_attachment" : "attachment", att.id, att.file_name);
                      }}
                      onDownload={() => handleDownload(att.id, attachmentsListRow.isReply ? "reply_attachment" : "attachment")}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailMailId && (
        <div
          className="modal d-block mails-detail-modal-backdrop"
          tabIndex="-1"
          onClick={closeMailDetail}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content mails-detail-modal">
              {detailLoading ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Loading mail...
                </div>
              ) : detailMail ? (
                <>
                  <div className="mails-detail-topbar">
                    <h5 className="mails-detail-subject">{detailMail.subject || "(No subject)"}</h5>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm mails-reply-btn"
                        onClick={() => openReplyWithMail(detailMail, "reply")}
                      >
                        <ReplyRoundedIcon style={{ fontSize: "16px" }} />
                        <span>Reply</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm mails-reply-btn"
                        onClick={() => openReplyWithMail(detailMail, "reply_all")}
                      >
                        <ReplyAllRoundedIcon style={{ fontSize: "16px" }} />
                        <span>Reply All</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm mails-reply-btn"
                        onClick={() => copyMailToClipboard(detailMail)}
                      >
                        <ContentCopyRoundedIcon style={{ fontSize: "16px" }} />
                        <span>Copy Mail</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm mails-reply-btn"
                        onClick={() => downloadMailZip(detailMail.id)}
                      >
                        <DownloadRoundedIcon style={{ fontSize: "16px" }} />
                        <span>Download Mail</span>
                      </button>
                      <button type="button" className="btn-close" onClick={closeMailDetail}></button>
                    </div>
                  </div>

                  <div className="mails-detail-headers">
                    <div
                      className="mails-avatar"
                      style={{ backgroundColor: getAvatarColor(detailMail.from_email) }}
                    >
                      {getInitial(detailMail.from_name, detailMail.from_email)}
                    </div>
                    <div className="mails-detail-headers-main">
                      <div className="mails-detail-headers-top">
                        <div className="mails-detail-sender">
                          <span className="mails-detail-sender-name">{detailMail.from_name || detailMail.from_email}</span>
                          <span className="mails-detail-sender-email">&lt;{detailMail.from_email}&gt;</span>
                        </div>
                        <div className="mails-detail-date">{formatDateTime(detailMail.received_at)}</div>
                      </div>
                      {detailMail.to_email && (
                        <div className="mails-detail-to">to {detailMail.to_email}</div>
                      )}
                      {detailMail.cc_email && (
                        <div className="mails-detail-to">cc {detailMail.cc_email}</div>
                      )}
                      <div className="mails-detail-badges">
                        <span className={`badge ${typeBadgeClass(detailMail.type)} px-3 py-1`}>
                          {detailMail.type === "payroll" ? "Payroll" : "Others"}
                        </span>
                        {detailMail.assigned_name && (
                          <span className="badge bg-primary-transparent text-primary px-3 py-1">
                            Assigned: {detailMail.assigned_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mails-detail-body">
                    {detailMail.body_html ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            resolveInlineImages(detailMail.body_html, detailMail.mail_uid),
                            { ADD_ATTR: ["target"] }
                          ),
                        }}
                      />
                    ) : detailMail.body_text ? (
                      <pre className="mails-detail-body-text">{detailMail.body_text}</pre>
                    ) : (
                      <p className="text-muted mb-0">No content found in this mail.</p>
                    )}
                  </div>

                  {((detailMail.attachments && detailMail.attachments.length > 0) || detailMail.invoice_file_name) && (
                    <div className="mails-detail-attachments">
                      {(detailMail.attachments || []).map((att) => (
                        <AttachmentTile
                          key={att.id}
                          name={att.file_name}
                          onView={() => {
                            closeMailDetail();
                            openPreview("attachment", att.id, att.file_name);
                          }}
                          onDownload={() => handleDownload(att.id)}
                        />
                      ))}
                      {detailMail.invoice_file_name && (
                        <AttachmentTile
                          name={detailMail.invoice_file_name}
                          label="(Invoice)"
                          onView={() => {
                            closeMailDetail();
                            openPreview("invoice", detailMail.id, detailMail.invoice_file_name);
                          }}
                        />
                      )}
                    </div>
                  )}

                  {detailMail.replies && detailMail.replies.length > 0 && (
                    <div className="mails-detail-replies">
                      {detailMail.replies.map((reply) => (
                        <div key={reply.id} className="mails-reply-card">
                          <div className="mails-reply-card-header">
                            <span className="mails-reply-card-sender">
                              {reply.sent_by_name || reply.sent_by}
                              <span className="mails-reply-card-to">to {reply.to_email}</span>
                              {reply.cc_email && (
                                <span className="mails-reply-card-to">cc {reply.cc_email}</span>
                              )}
                            </span>
                            <span className="mails-reply-card-date">{formatDateTime(reply.sent_at)}</span>
                          </div>
                          <div
                            className="mails-reply-card-body"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reply.body_html) }}
                          />
                          {reply.attachments && reply.attachments.length > 0 && (
                            <div className="mails-reply-card-attachments">
                              {reply.attachments.map((att) => (
                                <AttachmentTile
                                  key={att.id}
                                  name={att.file_name}
                                  onView={() => {
                                    closeMailDetail();
                                    openPreview("reply_attachment", att.id, att.file_name);
                                  }}
                                  onDownload={() => handleDownload(att.id, "reply_attachment")}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {detailMail.assignment_history && detailMail.assignment_history.length > 0 && (
                    <div className="mails-assignment-history">
                      <div className="mails-assignment-history-title">Assignment History</div>
                      {detailMail.assignment_history.map((h, idx) => (
                        <div key={idx} className="mails-assignment-history-row">
                          <span>
                            {h.assigned_to ? `Assigned to ${h.assigned_name || h.assigned_to}` : "Unassigned"}
                            {" — by "}
                            {h.changed_by_name || h.changed_by}
                          </span>
                          <span className="mails-assignment-history-date">{formatDateTime(h.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-muted text-center py-5">Could not load this mail.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {signatureModalOpen && (
        <div
          className="modal d-block mails-compose-modal"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1070 }}
          onClick={() => setSignatureModalOpen(false)}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate d-flex align-items-center gap-2">
                  <EditNoteRoundedIcon style={{ fontSize: "18px" }} />
                  My Signature
                </h5>
                <button type="button" className="btn-close" onClick={() => setSignatureModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-2" style={{ fontSize: "13px" }}>
                  This gets added to every reply you send automatically. Add text, bold/italic, colors, links, and
                  images (like your logo) using the toolbar below — click an inserted image to drag-resize it.
                </p>
                <ReactQuill
                  theme="snow"
                  value={signatureDraft}
                  onChange={setSignatureDraft}
                  modules={SIGNATURE_EDITOR_MODULES}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setSignatureDraft(defaultSignatureHtml())}
                >
                  Reset to default
                </button>
                <button type="button" className="btn btn-light" onClick={() => setSignatureModalOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={signatureSaving} onClick={saveSignature}>
                  {signatureSaving ? "Saving..." : "Save Signature"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {replyTarget && (
        <div
          className="modal d-block mails-compose-modal"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeReply}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate d-flex align-items-center gap-2">
                  <ReplyRoundedIcon style={{ fontSize: "18px" }} />
                  Reply
                </h5>
                <button type="button" className="btn-close" onClick={closeReply}></button>
              </div>
              <div className="modal-body">
                <div className="mails-compose-field-row">
                  <span className="mails-compose-field-label">To</span>
                  <span className="mails-compose-field-value">
                    {replyTarget.from_name ? `${replyTarget.from_name} <${replyTarget.from_email}>` : replyTarget.from_email}
                  </span>
                </div>
                <div className="mails-compose-field-row mails-compose-field-row-top">
                  <span className="mails-compose-field-label">Cc</span>
                  <textarea
                    ref={replyCcRef}
                    rows={1}
                    className="form-control form-control-sm border-0 px-0 mails-compose-field-value mails-compose-cc-textarea"
                    placeholder="Add Cc recipients, comma-separated"
                    value={replyCc}
                    onChange={(e) => {
                      setReplyCc(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                  />
                </div>
                <div className="mails-compose-field-row">
                  <span className="mails-compose-field-label">Subject</span>
                  <input
                    type="text"
                    className="form-control form-control-sm border-0 px-0 mails-compose-field-value"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                  />
                </div>

                <textarea
                  className="mails-compose-textarea"
                  placeholder="Write your reply... (you can paste a copied image or file here too)"
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  onPaste={handleReplyBodyPaste}
                />

                {savedSignature && (
                  <div className="mails-compose-signature-preview">
                    <div className="mails-compose-signature-preview-label">
                      Signature (added automatically)
                      <button
                        type="button"
                        className="mails-compose-signature-edit"
                        onClick={() => setSignaturePreviewExpanded((s) => !s)}
                      >
                        {signaturePreviewExpanded ? "Show less" : "Show more"}
                      </button>
                    </div>
                    <div
                      className={`mails-compose-signature-body ${signaturePreviewExpanded ? "mails-compose-signature-body-expanded" : ""}`}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(savedSignature) }}
                    />
                  </div>
                )}

                {replyAttachments.length > 0 && (
                  <div className="mails-compose-attachments">
                    {replyAttachments.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className="mails-compose-attachment-chip">
                        {fileIcon(file.name, "mails-attachment-icon")}
                        <span className="text-truncate" style={{ maxWidth: "160px" }}>
                          {file.name}
                        </span>
                        <button
                          type="button"
                          className="mails-compose-attachment-remove"
                          onClick={() => removeReplyAttachment(idx)}
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="d-flex align-items-center gap-2 mt-2">
                  <input
                    type="file"
                    id="reply-attachment-input"
                    className="d-none"
                    multiple
                    onChange={handleReplyAttachmentChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => document.getElementById("reply-attachment-input")?.click()}
                  >
                    <AttachFileRoundedIcon style={{ fontSize: "16px" }} /> Attach
                  </button>
                </div>

                <button
                  type="button"
                  className="mails-compose-quoted-toggle"
                  onClick={() => setShowQuoted((s) => !s)}
                >
                  {showQuoted ? "Hide" : "Show"} quoted original mail
                </button>
                {showQuoted && (
                  <div className="mails-compose-quoted">
                    <div className="mails-compose-quoted-meta">
                      On {formatDateTime(replyTarget.received_at)},{" "}
                      {replyTarget.from_name
                        ? `${replyTarget.from_name} <${replyTarget.from_email}>`
                        : replyTarget.from_email}{" "}
                      wrote:
                    </div>
                    {replyTarget.body_html ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            resolveInlineImages(replyTarget.body_html, replyTarget.mail_uid),
                            { ADD_ATTR: ["target"] }
                          ),
                        }}
                      />
                    ) : (
                      <pre className="mails-detail-body-text">{replyTarget.body_text}</pre>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeReply}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={sendingReply} onClick={sendReply}>
                  {sendingReply ? "Sending..." : "Send Reply"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Mails;
